import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import ActionConfirmationModal from 'ui-component/confirmation-dialogs/actionConfirmationModal/ActionConfirmationModal';
import { PiInfoFill, PiWarningFill } from 'react-icons/pi';
import { FaSave, FaSpinner } from 'react-icons/fa';
import AddRepairsModal from 'ui-component/confirmation-dialogs/addRepairsModal/AddRepairsModal';
import toast from 'react-hot-toast';
import PermissionAllowed from 'ui-component/permissions/PermissionAllowed';
import useSound from 'use-sound';
import usePageTitle from 'hooks/usePageTitle';

import errorSound from '../../assets/audio/error.mp3';
import DeleteConfirmation from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmation';

import Breadcrumbs from './components/Breadcrumbs';
import InboundingForm from './components/InboundingForm';
import Summary from './components/Summary';
import InboundList from './components/InboundList';
import Logs from './components/Logs';
import useIsMobile from 'utils/isMobileUtils';
import BreadcrumbsMobile from './components/BreadcrumbsMobile';
import InboundListMobile from './components/InboundListMobile';

// ✅ Clear
function OperationDetails({ edit }) {
    const { id } = useParams();
    const [setPageTitle] = usePageTitle('Operations');
    useEffect(() => {
        return () => {
            setPageTitle(null);
        };
    }, []);
    const history = useNavigate();
    const [errorSoundPlay] = useSound(errorSound);
    const [modelImeis, setModelImeis] = useState([]);
    const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);
    const [isDisable, setIsDisable] = useState(false);
    const [modelRepairOptions, setModelRepairOptions] = useState([]);
    const [selectedProductIndexes, setSelectedProductIndexes] = useState([]); // New state for selected products
    const [showDeleteBulkImeis, setShowDeleteBulkImeis] = useState(false);
    const [deleteRecordRefreshKey, setDeleteRecordRefreshKey] = useState(0);
    
    
    const [operationDetails, setOperationDetails] = useState({
        id: '',
        referenceNo: '',
        purchaseInvoiceId: '',
        status: '',
        action: '',
        type: '',
        products: [],
        logs: [], 
        invoice: {},
        sales_order: {},
        purchaseOrderNumber: '',
        supplier: {},
        warehouseId: ''
    });

    const [errorDialog, setErrorDialog] = useState({
        show: false,
        message: '',
        title: ''
    });

    const [confirmationDialog, setConfirmationDialog] = useState({
        show: false,
        message: '',
        title: '',
        handleConfirm: () => { },
        handleClose: () => { }
    });

    useEffect(() => {
        const data = {
            inboundId: id,
            modelImeis: modelImeis?.filter((m) => !m?.existing)
        };

        if (data?.modelImeis?.length > 0) {
            localStorage.setItem('inboundAutoSave', JSON.stringify(data));
        }

    }, [modelImeis]);

    const [addRepairDialog, setAddRepairDialog] = useState(null);

    const handleRepairDialogClose = () => {
        setAddRepairDialog(null);
    };

    const onAddRepairSubmit = (data) => {

        setModelImeis(
            modelImeis.map((item) => {
                if (item?.imei == data.imei) {
                    return data;
                }
                return item;
            })
        );

        if (modelImeis.find((item) => item?.imei == data.imei)?.existing) {
            setIsDisable(true);
            toast.promise(
                api
                    .updateStockRepair({
                        stock_id: modelImeis.find((item) => item?.imei == data.imei)?.stock_id,
                        repair_cost_ids: data?.repairs.map((r) => ({ id: r.id }))
                    })
                    .then((res) => {
                        if (res.status) {
                            setIsDisable(false);
                        }
                        return res?.status ? res : null;
                    }),
                {
                    loading: 'Updating Repairs...',
                    success: (data) => `${data.message}`,
                    error: <b>Failed to update repair</b>
                }
            );
        }

        handleRepairDialogClose();
    };

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            // Check if you want to show the confirmation popup
            if (true) {
                const message = 'Are you sure you want to leave?';
                event.returnValue = message; // Standard for most browsers
                return message; // For some older browsers
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);


    const handleCloseErrorDialog = () => {
        setErrorDialog({
            show: false,
            message: '',
            title: ''
        });
    };

    const showErrorDialog = (title, message = '') => {
        setErrorDialog({
            show: true,
            message: message,
            title: title
        });
    };


    useEffect(() => {
        if (id) {
            api.getOperationsDetails(id).then((res) => {
                if (res?.status) {
                    const data = {
                        id: res?.data?.id,
                        referenceNo: res?.data?.reference_number,
                        purchaseInvoiceId: res?.data?.purchase_invoice_id,
                        warehouseId: res?.data?.warehouse?.id,
                        action: 'view',
                        type: 'inbound',
					    status: res?.data?.status,
                        products: res?.data?.items,
                        logs: res?.data?.logs, 
                        invoice: res?.data?.invoice,
                        sales_order: res?.data?.sales_order,
                        purchaseOrderNumber: res?.data?.purchase_order_number,
                        supplier: res?.data?.supplier,
                    }
                    setOperationDetails(data);
                
                }
            });
        }
    }, []);



    const removeItem = (index) => {
        const updatedProducts = operationDetails?.products ? [...operationDetails.products] : [];
        updatedProducts.splice(index, 1);
        setOperationDetails({
            ...operationDetails,
            products: updatedProducts
        });
    }

    const handleRemoveProductFromList = (index) => {
        if (operationDetails?.products[index]?.id) {
         
            toast.promise(
                api
                    .deleteOperationInboundItem(operationDetails?.products[index]?.id)
                    .then(
                        (res) => {
                            if (res.status) {
                                removeItem(index)
                            }

                            return res?.status ? res : null;
                        },
                        () => { }
                    ),
                {
                    loading: 'Removing IMEI...',
                    success: (data) => `${data.message}`,
                    error: <b>Failed to remove IMEI</b>
                }
            );
        } else {
            removeItem(index)
        }
    }

    const handleSelectProductsFromList = (indexes) => {
        setSelectedProductIndexes(indexes);
    }

    const handleDeleteBulkImeis = () => {
        if (selectedProductIndexes?.length > 0) {
            let compiledInboundedItems = [];
            let indexesToRemove = [];
            selectedProductIndexes?.forEach((index) => {
                if (operationDetails?.products[index]?.id) {
                    compiledInboundedItems.push({ id: operationDetails?.products[index]?.id });
                } else {
                    indexesToRemove.push(index);
                }
            });
            if (compiledInboundedItems?.length > 0) {
                toast.promise(
                    api
                        .deleteBulkOperationInboundItems(id, {items: compiledInboundedItems})
                        .then(
                            (res) => {
                                if (res.status) {
                                    let updatedProducts = operationDetails?.products ? [...operationDetails.products] : [];
                                    const sortedIndexes = [...selectedProductIndexes].sort((a, b) => b - a);
                                    sortedIndexes.forEach((index) => {
                                        updatedProducts.splice(index, 1);
                                    });
                                    setOperationDetails({
                                        ...operationDetails,
                                        products: updatedProducts
                                    });

                                    setSelectedProductIndexes([]);
                                    setShowDeleteBulkImeis(false);
                                }

                                return res?.status ? res : null;
                            },
                            () => { }
                        ),
                    {
                        loading: 'Removing IMEIs...',
                        success: (data) => `${data.message}`,
                        error: <b>Failed to remove IMEIs</b>
                    }
                );
            } 
            if (indexesToRemove.length > 0) {
                // Sort descending to avoid index shift
                const updatedProducts = operationDetails?.products ? [...operationDetails.products] : [];
                indexesToRemove.sort((a, b) => b - a).forEach((index) => {
                    updatedProducts.splice(index, 1);
                });
                setOperationDetails({
                    ...operationDetails,
                    products: updatedProducts
                });
            }
            setDeleteRecordRefreshKey(prev => prev + 1);
            setShowDeleteBulkImeis(false);
        }
    }

    const handleErrorDialog = (data) => {
        setErrorDialog({
            ...errorDialog,
            show: data?.show,
            message: data?.message,
            title: data?.title
        })
        errorSoundPlay();

    }

    const handleUpdateOperationDetailsProducts = (details) => { 
        const updatedProducts = operationDetails?.products ? [...operationDetails.products] : [];
        
        updatedProducts.push(details);
        setOperationDetails({
            ...operationDetails,
            products: updatedProducts
        });
    }
    const handleBulkUpdateOperationDetailsProducts = (details) => { 
        const flatDetails = details.flat ? details.flat() : [].concat(...details);
        const updatedProducts = operationDetails?.products ? [...operationDetails.products] : [];
        updatedProducts.push(...flatDetails);
        setOperationDetails({
            ...operationDetails,
            products: updatedProducts
        });
    }

    const saveOperationDetails = (action) => {
        if (!operationDetails?.warehouseId) {
            toast.error('Please select a warehouse');
            return;
        }
        setIsSubmitInProgress(true);
        const collection = {
            id: operationDetails?.id,
            purchase_invoice_id: operationDetails?.purchaseInvoiceId,
            warehouse_id: operationDetails?.warehouseId,
            status: operationDetails?.status,
            action: action,
            type: 'create',
            products: operationDetails?.products
        }
        if (edit) {
            
            toast.promise(
                api.updateOperationsData(id, collection).then((res) => {
                    if (res.status) {
                        setIsDisable(false);
                        history(`/operations/view/${res?.data?.id}/qualitycontrol`);
                    }
                    return res?.status ? res : null;
                }),
                {
                    loading: 'Updating operation...',
                    success: (data) => `${data.message}`,
                    error: (data) => {
                        setIsDisable(false);
                        return <b>Failed to update operation</b>;
                    }
                }
            );
        } else {

            api.storeOperationsData(collection).then((res) => {
                if (res?.status) {
                    toast.success(res.message);
                    history(`/operations/view/${res?.data?.id}/qualitycontrol`);
                } else {
                    toast.error(res.message);
                }
            },
            () => { }
        );
            
        }


    }

    const isMobile = useIsMobile();

	return (
		<>
			<div className="row m-0 w-100" style={{ marginTop: '20px' }}>
				<div className={`col-12 col-md-12 ${isMobile ? 'p-0' : ''}`}>
					<div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
						<div className="header-bubble" />
						<div className="d-flex flex-column " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
							<div
								style={{
									position: 'relative',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									marginBottom: 8,
									minHeight: 38 // ensures enough height for the button
								}}
							>
								{' '}
								<button
									type="button"
									aria-label="Back"
									onClick={() => history(-1)}
									className="circle-back-btn"
									style={{
										position: 'absolute',
										left: 20,
										top: '50%',
										transform: 'translateY(-50%)'
									}}
								>
									<svg width="22" height="22" fill="none" viewBox="0 0 24 24">
										<path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
								</button>
								<h5
									style={{
										textAlign: 'center',
										flex: 1,
										margin: 0,
										display: 'flex',
										gap: 10,
										justifyContent: 'center',
										alignItems: 'center'
									}}
								>
									{' '}
									{operationDetails?.referenceNo ? operationDetails?.referenceNo : 'New Inbound'}{' '}
								</h5>
							</div>
							<div style={{ display: 'flex', justifyContent: 'center', width: 'center' }}>
								{!edit ? (
                                    <PermissionAllowed permissions={['Create Operations']}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (!isSubmitInProgress) {
                                                    saveOperationDetails('to_qualitycontrol');
                                                }
                                            }}
                                            className="btn btn-primary btn-outlined-main"
                                            disabled={isSubmitInProgress || operationDetails?.products?.length == 0}
                                        >
                                            {isSubmitInProgress ? (
                                                <>
                                                    <FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave /> Save
                                                </>
                                            )}
                                        </button>
                                    </PermissionAllowed>
								) : (
									<button
										type="button"
										onClick={() => {
											if (!isSubmitInProgress) {
												saveOperationDetails('update');
												// setIsSubmitInProgress(true);
											}
										}}
										className="btn btn-primary btn-outlined-main"
										disabled={isSubmitInProgress || operationDetails?.products?.length == 0 || isDisable}
									>
										{isSubmitInProgress ? (
											<>
												<FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Saving...
											</>
										) : (
											<>
												<FaSave /> Update
											</>
										)}
									</button>
								)}
							</div>
						</div>
					</div>
					{isMobile && (
						<div >
							<BreadcrumbsMobile
								current={`inbound`}
								activateTabsUsingId={operationDetails?.id}
								referenceNumber={operationDetails?.referenceNo}
							/>
						</div>
					)}
					<div className="section-head hide-on-mobile" style={{ paddingLeft: '0' }}>
						<h2>
							<div className="actions" style={{ display: 'flex', width: '100%' }}>
								<Breadcrumbs
									current={`inbound`}
									activateTabsUsingId={operationDetails?.id}
									referenceNumber={operationDetails?.referenceNo}
								/>
							</div>
						</h2>
						<div className="actions"></div>
					</div>
					<div className={`row mobile-scroll-container ${isMobile ? 'mobile-scroll-container-xsmaller m-0' : ''}`}>
						<div className={`col-lg-8 col-12 form-group ${isMobile ? 'p-0' : ''}`}>
							<div className={`${isMobile ? '' : 'card shadow-sm bg-white table-card p-3'}`} style={{ marginTop: '0' }}>
								<div className={`${isMobile ? '' : 'bg-white pt-0 p-3'}`} style={{ marginTop: isMobile ? '' : '-15px' }}>
									<div className={`row ${isMobile ? 'mb-3 mx-0 p-3 p-xl-4 w-100 new-card' : ''}`}>
										<div className="col-12 form-group">
											<InboundingForm
												operationDetails={operationDetails}
												handleErrorDialog={handleErrorDialog}
												handleUpdateOperationDetailsProducts={handleUpdateOperationDetailsProducts}
                                                handleBulkUpdateOperationDetailsProducts={handleBulkUpdateOperationDetailsProducts}
                                                handleUpdatePurchaseOrder={(value) => {
                                                    setOperationDetails({
                                                        ...operationDetails,
                                                        purchaseInvoiceId: value,
                                                    })
                                                }}
                                                handleUpdateWarehouse={(value) => {
                                                    setOperationDetails({
                                                        ...operationDetails,
                                                        warehouseId: value,
                                                    })
                                                }}
											/>
										</div>
									</div>
									{!isMobile ? (
										<div className="row">
											<div className="col-12">
                                                <InboundList
                                                    operationDetails={operationDetails}
                                                    handleRemoveProductFromList={handleRemoveProductFromList}
                                                    handleSelectProductsFromList={handleSelectProductsFromList}
                                                    deleteRecordRefreshKey={deleteRecordRefreshKey}
                                                />
											</div>
										</div>
									) : (
										<InboundListMobile operationDetails={operationDetails} handleRemoveProductFromList={handleRemoveProductFromList} />
									)}
									<div className="row hide-on-mobile">
										<div className="col-12 d-flex justify-content-between gap-2">
											{!edit ? (
												<>
                                                    <div className="flex gap-3 align-items-center">
                                                        {
                                                            selectedProductIndexes?.length > 0 && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setShowDeleteBulkImeis(true);
                                                                        }}
                                                                        className="btn btn-primary btn-outlined-danger"
                                                                        disabled={isSubmitInProgress || operationDetails?.products?.length == 0 || isDisable}
                                                                    >
                                                                        {isSubmitInProgress ? (
                                                                            <>
                                                                                <FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Saving...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FaSave /> Delete Selected
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                    <div>
                                                                        Total IMEI Selected: {selectedProductIndexes?.length}
                                                                    </div>
                                                                </>
                                                            )
                                                        }
                                                    </div>
													<PermissionAllowed permissions={['Create Operations']}>
														<button
															type="button"
															onClick={() => {
																if (!isSubmitInProgress) {
																	saveOperationDetails('to_qualitycontrol');
																}
															}}
															className="btn btn-primary btn-outlined-main"
															disabled={isSubmitInProgress || operationDetails?.products?.length == 0}
														>
															{isSubmitInProgress ? (
																<>
																	<FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Saving...
																</>
															) : (
																<>
																	<FaSave /> Save
																</>
															)}
														</button>
													</PermissionAllowed>
												</>
                                            ) : (
                                                <>
                                                    <div className="flex gap-3 align-items-center">
                                                        {
                                                            selectedProductIndexes?.length > 0 && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                            onClick={() => {
                                                                            setShowDeleteBulkImeis(true);
                                                                        }}
                                                                        className="btn btn-primary btn-outlined-danger"
                                                                        disabled={isSubmitInProgress || operationDetails?.products?.length == 0 || isDisable}
                                                                    >
                                                                        {isSubmitInProgress ? (
                                                                            <>
                                                                                <FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Saving...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FaSave /> Delete Selected
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                    <div>
                                                                        Total IMEI Selected: {selectedProductIndexes?.length}
                                                                    </div>
                                                                </>
                                                            )
                                                        }
                                                    </div>
                                                            
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (!isSubmitInProgress) {
                                                                saveOperationDetails('update');
                                                            }
                                                        }}
                                                        className="btn btn-primary btn-outlined-main"
                                                        disabled={isSubmitInProgress || operationDetails?.products?.length == 0 || isDisable}
                                                    >
                                                        {isSubmitInProgress ? (
                                                            <>
                                                                <FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaSave /> Update
                                                            </>
                                                        )}
                                                    </button>
                                                </>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-lg-4">
							<div className="row">
								<div className={`col-12 form-group ${isMobile ? 'm-0 p-0' : ''}`}>
									<Summary operationDetails={operationDetails} />
									{!isMobile && (<Logs logs={operationDetails?.logs} />)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			{errorDialog?.show && (
				<ActionConfirmationModal
					show={errorDialog?.show}
					message={errorDialog?.message}
					title={errorDialog?.title}
					noCancel
					customConfirmText="Okay"
					customIcon={<PiWarningFill />}
					handleConfirm={() => {
						handleCloseErrorDialog();
					}}
					handleClose={() => {
						handleCloseErrorDialog();
					}}
				/>
			)}

			{confirmationDialog?.show && (
				<ActionConfirmationModal
					show={confirmationDialog?.show}
					message={confirmationDialog?.message}
					title={confirmationDialog?.title}
					customConfirmText={confirmationDialog?.customConfirm || 'Confirm'}
					customCancelText={confirmationDialog?.customCancel || 'Cancel'}
					customIcon={<PiInfoFill />}
					handleConfirm={confirmationDialog?.handleConfirm}
					handleClose={confirmationDialog?.handleClose}
				/>
			)}

			{addRepairDialog && (
				<AddRepairsModal
					show={addRepairDialog}
					handleClose={handleRepairDialogClose}
					onSubmit={onAddRepairSubmit}
					repairOptions={modelRepairOptions?.find((m) => m?.model_id == addRepairDialog?.product?.model?.id)?.options}
				/>
			)}
			{showDeleteBulkImeis && (
				<DeleteConfirmation
					show={showDeleteBulkImeis}
                    handleClose={() => {
                        setShowDeleteBulkImeis(false);
                    }}
					handleDelete={handleDeleteBulkImeis}
					title="Confirm"
					message={`Are you sure you want to delete selected IMEIs from the list?`}
				/>
			)}
		</>
	);
}

export default OperationDetails;
